import { useAuiState } from '@assistant-ui/react';

import { isPartStatusCancelled, isPartStatusError } from '#plugin-chat/hooks/isErroredToolPart.ts';
import { useToolPart } from '#plugin-chat/hooks/useToolPart.ts';

export function usePartStatus() {
  const status = useAuiState((state) => state.part.status);
  const interrupt = useAuiState((state) => (state.part.type === 'tool-call' ? state.part.interrupt : undefined));
  // Tool-call parts carry a separate `isError` flag (assistant-ui core message
  // type), distinct from the part's status. JSON-parse failures and SDK-level
  // tool-input validation errors surface here with `status.type === 'complete'`,
  // so we MUST check `isError` independently — otherwise an errored tool call
  // renders as "Complete ✓" with empty content.
  const partIsError = useAuiState((state) => (state.part.type === 'tool-call' ? state.part.isError === true : false));

  // Detect interrupt state: The AISDKMessageConverter sets status.type='requires-action' and status.reason='interrupt'
  // on tool-call parts when toolStatuses indicates an interrupt. We check both the computed status and the interrupt
  // field since the MessageRuntime may use either source depending on the code path.
  const isInterruptStatus = status?.type === 'requires-action' && 'reason' in status && status.reason === 'interrupt';
  const isAwaitingInput = isInterruptStatus || !!interrupt;

  const { result } = useToolPart<Record<string, unknown>, unknown>();
  const isErrorResponse = result && typeof result === 'object' && 'success' in result && result.success === false;

  const isError = isPartStatusError(status) || partIsError || isErrorResponse;

  return {
    isRunning: status?.type === 'running' && !isAwaitingInput,
    // A part with `isError: true` should NOT report as complete, even if the
    // status type is "complete" — downstream UIs (e.g. dashboard renderers)
    // would otherwise paint stale or empty content over an error.
    isComplete: status?.type === 'complete' && !partIsError,
    isCancelled: isPartStatusCancelled(status),
    isError,
    isAwaitingInput,
  };
}
